import { useEffect, useState } from 'react';
import { useToast } from '../../components/ToastContainer';
import ToastContainer from '../../components/ToastContainer';
import { MdInfo, MdNotifications, MdSettings, MdTune } from 'react-icons/md';
import { t, setLanguage, getLanguage } from '../../utils/i18n';
import { getPaymentReminderSettings, updatePaymentReminderSettings } from '../../services/api';
import { getCurrency, setCurrency } from '../../utils/currency';
import { getBuildInfo, fetchBuildInfoFromServer } from '../../utils/buildInfo';

const defaultSettings = {
  reminder3d: true,
  reminder0d: true,
  reminderPlus3d: true,
  emailEnabled: true,
  notificationEnabled: true,
};

export default function NotificationSettingsPage() {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const { showToast, removeToast, toasts } = useToast();
  const [buildInfo, setBuildInfo] = useState(() => getBuildInfo());
  const [showCurrencyConversion, setShowCurrencyConversion] = useState(false);
  const [currencyConversion, setCurrencyConversion] = useState({
    oldCurrency: '',
    newCurrency: '',
    exchangeRate: ''
  });
  const [currentCurrency, setCurrentCurrency] = useState(getCurrency());

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getPaymentReminderSettings();
        setSettings({ ...defaultSettings, ...data });
      } catch (error) {
        console.error('Failed to load reminder settings', error);
        showToast(t('settings.notifications.error') || 'Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [showToast]);

  useEffect(() => {
    fetchBuildInfoFromServer().then(setBuildInfo);
  }, []);

  const handleCheckboxChange = (key) => async (event) => {
    const newValue = event.target.checked;
    const updatedSettings = {
      ...settings,
      [key]: newValue,
    };
    setSettings(updatedSettings);
    
    // Auto-save immediately
    try {
      await updatePaymentReminderSettings(updatedSettings);
      showToast(t('settings.notifications.saved') || 'Podešavanja su sačuvana', 'success');
    } catch (error) {
      console.error('Failed to update reminder settings', error);
      showToast(t('settings.notifications.error') || 'Greška pri čuvanju podešavanja', 'error');
      // Revert on error
      setSettings(settings);
    }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 p-4 lg:p-6 space-y-6 pb-6 max-w-7xl mx-auto w-full">
        <div className="surface-panel flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary-600/10 flex items-center justify-center">
              <MdSettings className="text-primary-600 dark:text-primary-400 text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('settings.title') || 'Podešavanja'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
                {t('settings.description') || 'Upravljajte podsetnicima, obaveštenjima i opštim postavkama.'}
              </p>
            </div>
          </div>
        </div>

        {/* Reminders Section */}
        <div className="card">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center">
                <MdNotifications className="text-primary-600 dark:text-primary-400 text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('settings.reminders.title') || 'Podsetnici'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings.reminders.description') || 'Podesite obaveštenja za dospeća i podsetnike.'}
                </p>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  {t('settings.notifications.description')}
                </p>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={settings.reminder3d}
                      onChange={handleCheckboxChange('reminder3d')}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('settings.notifications.reminder3d')}
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={settings.reminder0d}
                      onChange={handleCheckboxChange('reminder0d')}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('settings.notifications.reminder0d')}
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={settings.reminderPlus3d}
                      onChange={handleCheckboxChange('reminderPlus3d')}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('settings.notifications.reminderPlus3d')}
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-white/60 dark:border-gray-700/60">
                <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-primary-600"
                    checked={settings.emailEnabled}
                    onChange={handleCheckboxChange('emailEnabled')}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold block">{t('settings.notifications.emailLabel')}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('settings.notifications.emailDescription')}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-primary-600"
                    checked={settings.notificationEnabled}
                    onChange={handleCheckboxChange('notificationEnabled')}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold block">{t('settings.notifications.inAppLabel')}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('settings.notifications.inAppDescription')}
                    </span>
                  </span>
                </label>
              </div>

            </div>
          )}
        </div>

        {/* General Settings Section */}
        <div className="card">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center">
                <MdTune className="text-primary-600 dark:text-primary-400 text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('settings.general.title') || 'Opšta podešavanja'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings.general.description') || 'Izaberite jezik, temu i format prikaza.'}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.general.theme') || 'Tema'}
              </label>
              <select
                className="w-full px-3 py-2 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={localStorage.getItem('theme') || 'system'}
                onChange={(e) => {
                  const theme = e.target.value;
                  
                  if (theme === 'dark') {
                    localStorage.setItem('theme', 'dark');
                    document.documentElement.classList.add('dark');
                  } else if (theme === 'light') {
                    localStorage.setItem('theme', 'light');
                    document.documentElement.classList.remove('dark');
                  } else {
                    // system
                    localStorage.removeItem('theme'); // Remove to use system preference
                    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                    if (prefersDark) {
                      document.documentElement.classList.add('dark');
                    } else {
                      document.documentElement.classList.remove('dark');
                    }
                  }
                  showToast((t('settings.general.theme') || 'Tema') + ' ' + (t('settings.notifications.saved') || 'sačuvano'), 'success');
                }}
              >
                <option value="light">{t('settings.general.light') || 'Svetla'}</option>
                <option value="dark">{t('settings.general.dark') || 'Tamna'}</option>
                <option value="system">{t('settings.general.system') || 'Sistemska'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.general.language') || 'Jezik'}
              </label>
              <select
                className="w-full px-3 py-2 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={getLanguage()}
                onChange={(e) => {
                  const lang = e.target.value;
                  setLanguage(lang);
                  showToast((t('settings.general.language') || 'Jezik') + ' ' + (t('settings.notifications.saved') || 'sačuvano'), 'success');
                }}
              >
                <option value="sr">{t('settings.general.serbian') || 'Srpski'}</option>
                <option value="en">{t('settings.general.english') || 'English'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.general.currency') || 'Valuta'}
              </label>
              <select
                className="w-full px-3 py-2 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={currentCurrency}
                onChange={(e) => {
                  const oldCurrency = currentCurrency;
                  const newCurrency = e.target.value;
                  
                  if (oldCurrency !== newCurrency) {
                    // Show conversion modal
                    setCurrencyConversion({
                      oldCurrency,
                      newCurrency,
                      exchangeRate: ''
                    });
                    setShowCurrencyConversion(true);
                  } else {
                    setCurrentCurrency(newCurrency);
                    setCurrency(newCurrency);
                    showToast((t('settings.general.currency') || 'Valuta') + ' ' + (t('settings.notifications.saved') || 'sačuvano'), 'success');
                  }
                }}
              >
                <option value="RSD">RSD (Srpski dinar)</option>
                <option value="EUR">EUR (Euro)</option>
                <option value="USD">USD (US Dollar)</option>
                <option value="GBP">GBP (British Pound)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.general.currencyFormat') || 'Format valute'}
              </label>
              <select
                className="w-full px-3 py-2 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                defaultValue={localStorage.getItem('currencyFormat') || 'european'}
                onChange={(e) => {
                  localStorage.setItem('currencyFormat', e.target.value);
                  window.dispatchEvent(new CustomEvent('currencyFormatChanged', { detail: { format: e.target.value } }));
                  showToast((t('settings.general.currencyFormat') || 'Format valute') + ' ' + (t('settings.notifications.saved') || 'sačuvano'), 'success');
                }}
              >
                <option value="european">123.456,00 (Evropski)</option>
                <option value="us">123,456.00 (Američki)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.general.dateFormat') || 'Format datuma'}
              </label>
              <select
                className="w-full px-3 py-2 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                defaultValue={localStorage.getItem('dateFormat') || 'dd.mm.yyyy'}
                onChange={(e) => {
                  localStorage.setItem('dateFormat', e.target.value);
                  window.dispatchEvent(new CustomEvent('dateFormatChanged', { detail: { format: e.target.value } }));
                  showToast((t('settings.general.dateFormat') || 'Format datuma') + ' ' + (t('settings.notifications.saved') || 'sačuvano'), 'success');
                }}
              >
                <option value="dd.mm.yyyy">DD.MM.YYYY (npr. 17.11.2025)</option>
                <option value="mm/dd/yyyy">MM/DD/YYYY (npr. 11/17/2025)</option>
                <option value="yyyy-mm-dd">YYYY-MM-DD (npr. 2025-11-17)</option>
              </select>
            </div>
          </div>
        </div>

        {/* App Version Section */}
        <div className="card">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center">
                <MdInfo className="text-primary-600 dark:text-primary-400 text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('settings.version.title') || 'Informacije o aplikaciji'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings.version.description') || 'Trenutna verzija i datum poslednjeg deploy-a.'}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                {t('settings.version.version') || 'Verzija'}
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100 font-mono">
                {buildInfo.version}
              </p>
            </div>
            <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                {t('settings.version.deployed') || 'Postavljeno'}
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100 font-mono">
                {buildInfo.deployDate || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Currency Conversion Modal */}
      {showCurrencyConversion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="card max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('settings.currencyConversion.title') || 'Konverzija valute'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('settings.currencyConversion.description') || `Menjate valutu sa ${currencyConversion.oldCurrency} na ${currencyConversion.newCurrency}. Unesite kurs konverzije da bi se iznosi prikazali ispravno.`}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings.currencyConversion.exchangeRate') || 'Kurs konverzije'}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('settings.currencyConversion.exchangeRateHint') || `1 ${currencyConversion.oldCurrency} = ? ${currencyConversion.newCurrency}`}
                </p>
                <input
                  type="number"
                  step="0.0001"
                  value={currencyConversion.exchangeRate}
                  onChange={(e) => setCurrencyConversion({ ...currencyConversion, exchangeRate: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (currencyConversion.exchangeRate && parseFloat(currencyConversion.exchangeRate) > 0) {
                      // Store exchange rate for future conversions
                      localStorage.setItem(`exchangeRate_${currencyConversion.oldCurrency}_${currencyConversion.newCurrency}`, currencyConversion.exchangeRate);
                      setCurrentCurrency(currencyConversion.newCurrency);
                      setCurrency(currencyConversion.newCurrency);
                      setShowCurrencyConversion(false);
                      showToast(t('settings.currencyConversion.success') || 'Valuta je uspešno promenjena', 'success');
                    } else {
                      // Change currency without conversion
                      setCurrentCurrency(currencyConversion.newCurrency);
                      setCurrency(currencyConversion.newCurrency);
                      setShowCurrencyConversion(false);
                      showToast(t('settings.currencyConversion.noRate') || 'Valuta je promenjena bez konverzije iznosa', 'info');
                    }
                  }}
                  className="btn-primary flex-1"
                >
                  {t('common.save') || 'Sačuvaj'}
                </button>
                <button
                  onClick={() => {
                    setShowCurrencyConversion(false);
                    // Reset select to old currency
                    setCurrentCurrency(currencyConversion.oldCurrency);
                  }}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
